import { Type, type Static } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { hasUnicodeControlCharacters, unicodeCodePointLength } from "./unicode-text.ts";

export const KNOWLEDGE_QUERY_COMPILATION_SCHEMA_VERSION = "2.0" as const;
export const KNOWLEDGE_BASE_DIRECTORY_LIMITS = {
  maxEntries: 64,
  maxDisplayNameChars: 64,
  maxDescriptionChars: 1024,
  maxTotalDescriptionChars: 32_768
} as const;

export const KnowledgeBaseDirectoryEntryV2Schema = Type.Object(
  {
    knowledgeBaseId: Type.String({ minLength: 1, maxLength: 128 }),
    displayName: Type.String({ minLength: 1, maxLength: 64 }),
    description: Type.String({ minLength: 1, maxLength: 1024 })
  },
  { additionalProperties: false }
);

export const KnowledgeBaseDirectoryV2Schema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QUERY_COMPILATION_SCHEMA_VERSION),
    knowledgeBases: Type.Array(KnowledgeBaseDirectoryEntryV2Schema, {
      minItems: 0,
      maxItems: KNOWLEDGE_BASE_DIRECTORY_LIMITS.maxEntries
    })
  },
  { additionalProperties: false }
);

export const CompiledKnowledgeQueryPlanV2Schema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QUERY_COMPILATION_SCHEMA_VERSION),
    standaloneQuestion: Type.String({ minLength: 1, maxLength: 4096 }),
    selectedKnowledgeBaseIds: Type.Array(Type.String({ minLength: 1, maxLength: 128 }), {
      minItems: 1,
      maxItems: 3,
      uniqueItems: true
    })
  },
  { additionalProperties: false }
);

export type KnowledgeBaseDirectoryEntryV2 = Static<typeof KnowledgeBaseDirectoryEntryV2Schema>;
export type KnowledgeBaseDirectoryV2 = Static<typeof KnowledgeBaseDirectoryV2Schema>;
export type CompiledKnowledgeQueryPlanV2 = Static<typeof CompiledKnowledgeQueryPlanV2Schema>;

export function decodeKnowledgeBaseDirectoryV2(value: unknown): KnowledgeBaseDirectoryV2 {
  if (!Value.Check(KnowledgeBaseDirectoryV2Schema, value)) {
    throw new Error("Knowledge Base directory violates the shared schema");
  }
  const directory = value as KnowledgeBaseDirectoryV2;
  const ids = directory.knowledgeBases.map((entry) => entry.knowledgeBaseId);
  if (new Set(ids).size !== ids.length) {
    throw new Error("Knowledge Base directory contains duplicate IDs");
  }
  let totalDescriptionChars = 0;
  for (const entry of directory.knowledgeBases) {
    if (entry.knowledgeBaseId !== entry.knowledgeBaseId.trim() || !entry.knowledgeBaseId.trim()) {
      throw new Error("Knowledge Base directory contains an invalid ID");
    }
    if (
      entry.displayName !== entry.displayName.trim() ||
      !entry.displayName.trim() ||
      unicodeCodePointLength(entry.displayName) >
        KNOWLEDGE_BASE_DIRECTORY_LIMITS.maxDisplayNameChars ||
      hasUnicodeControlCharacters(entry.displayName)
    ) {
      throw new Error("Knowledge Base directory contains an invalid display name");
    }
    if (entry.description !== entry.description.trim() || !entry.description.trim()) {
      throw new Error("Knowledge Base directory contains an invalid description");
    }
    const descriptionChars = unicodeCodePointLength(entry.description);
    if (descriptionChars > KNOWLEDGE_BASE_DIRECTORY_LIMITS.maxDescriptionChars) {
      throw new Error("Knowledge Base directory description exceeds its character budget");
    }
    totalDescriptionChars += descriptionChars;
  }
  if (totalDescriptionChars > KNOWLEDGE_BASE_DIRECTORY_LIMITS.maxTotalDescriptionChars) {
    throw new Error("Knowledge Base directory exceeds its total character budget");
  }
  return structuredClone(directory);
}

export function decodeCompiledKnowledgeQueryPlanV2(
  value: unknown,
  options: { directoryIds: ReadonlySet<string>; maxSelectedKnowledgeBases: number }
): CompiledKnowledgeQueryPlanV2 {
  if (!Value.Check(CompiledKnowledgeQueryPlanV2Schema, value)) {
    throw new Error("compiled Knowledge Query Plan violates the shared schema");
  }
  const plan = value as CompiledKnowledgeQueryPlanV2;
  if (plan.standaloneQuestion !== plan.standaloneQuestion.trim()) {
    throw new Error("compiled standalone question must be normalized");
  }
  if (
    !Number.isInteger(options.maxSelectedKnowledgeBases) ||
    options.maxSelectedKnowledgeBases < 1 ||
    plan.selectedKnowledgeBaseIds.length > options.maxSelectedKnowledgeBases
  ) {
    throw new Error("compiled Knowledge Query Plan selects too many Knowledge Bases");
  }
  if (plan.selectedKnowledgeBaseIds.some((id) => !options.directoryIds.has(id))) {
    throw new Error("compiled Knowledge Query Plan selects an unknown Knowledge Base");
  }
  return {
    ...structuredClone(plan),
    selectedKnowledgeBaseIds: [...plan.selectedKnowledgeBaseIds].sort((left, right) =>
      left.localeCompare(right)
    )
  };
}
